package com.mgacreative.hotelru.domain.repository

import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.domain.model.Guest
import com.mgacreative.hotelru.domain.model.Reservation
import com.mgacreative.hotelru.domain.model.Room
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class ReservationRepository(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession? = null
) {
    private val demoGuests = listOf(
        Guest(id = "demo-g-1", tenantId = "demo-tenant-isolated", firstName = "Иван", lastName = "Иванов", idPassportNo = "4510 123456", country = "Россия", phone = "+7 (916) 123-45-67", email = "ivanov@mail.ru"),
        Guest(id = "demo-g-2", tenantId = "demo-tenant-isolated", firstName = "Елена", lastName = "Смирнова", idPassportNo = "4512 654321", country = "Россия", phone = "+7 (925) 987-65-43", email = "smirnova@yandex.ru"),
        Guest(id = "demo-g-3", tenantId = "demo-tenant-isolated", firstName = "Алексей", lastName = "Петров", idPassportNo = "4518 789012", country = "Россия", phone = "+7 (903) 555-11-22", email = "petrov@inbox.ru"),
        Guest(id = "demo-g-4", tenantId = "demo-tenant-isolated", firstName = "Анна", lastName = "Кузнецова", idPassportNo = "4520 345678", country = "Россия", phone = "+7 (910) 444-33-22", email = "kuznetsova@gmail.com")
    )

    private val demoReservations = mutableListOf(
        Reservation(
            id = "demo-res-1",
            tenantId = "demo-tenant-isolated",
            guestId = "demo-g-1",
            roomId = "demo-102",
            checkInDate = "2026-09-08",
            checkOutDate = "2026-09-12",
            status = "OCCUPIED",
            roomType = "Superior",
            totalPrice = 24000.0,
            specialNotes = "Поздний выезд, высокий этаж",
            adultsCount = 2,
            childrenCount = 0,
            guests = demoGuests[0],
            rooms = Room(id = "demo-102", tenantId = "demo-tenant-isolated", roomNumber = "102", roomType = "Superior", status = "OCCUPIED")
        ),
        Reservation(
            id = "demo-res-2",
            tenantId = "demo-tenant-isolated",
            guestId = "demo-g-2",
            roomId = "demo-101",
            checkInDate = "2026-09-09",
            checkOutDate = "2026-09-15",
            status = "CONFIRMED",
            roomType = "Standard",
            totalPrice = 18000.0,
            specialNotes = "Тихий номер, детская кроватка",
            adultsCount = 2,
            childrenCount = 1,
            guests = demoGuests[1],
            rooms = Room(id = "demo-101", tenantId = "demo-tenant-isolated", roomNumber = "101", roomType = "Standard", status = "CLEAN")
        ),
        Reservation(
            id = "demo-res-3",
            tenantId = "demo-tenant-isolated",
            guestId = "demo-g-3",
            roomId = "demo-202",
            checkInDate = "2026-09-10",
            checkOutDate = "2026-09-14",
            status = "CONFIRMED",
            roomType = "Executive",
            totalPrice = 42000.0,
            specialNotes = "VIP трансфер из аэропорта",
            adultsCount = 1,
            childrenCount = 0,
            guests = demoGuests[2],
            rooms = Room(id = "demo-202", tenantId = "demo-tenant-isolated", roomNumber = "202", roomType = "Executive", status = "CLEAN")
        ),
        Reservation(
            id = "demo-res-4",
            tenantId = "demo-tenant-isolated",
            guestId = "demo-g-4",
            roomId = "demo-103",
            checkInDate = "2026-09-05",
            checkOutDate = "2026-09-09",
            status = "CHECKED_OUT",
            roomType = "Suite",
            totalPrice = 35000.0,
            specialNotes = "Оплата картой Мир завершена",
            adultsCount = 2,
            childrenCount = 0,
            guests = demoGuests[3],
            rooms = Room(id = "demo-103", tenantId = "demo-tenant-isolated", roomNumber = "103", roomType = "Suite", status = "DIRTY")
        )
    )

    suspend fun getReservations(): List<Reservation> {
        if (tenantSession?.isDemoMode == true) {
            return demoReservations.toList()
        }
        return try {
            networkClient.client.postgrest["reservations"]
                .select(io.github.jan.supabase.postgrest.query.Columns.raw("*, guests(*), rooms(*)"))
                .decodeList<Reservation>()
        } catch (e: Exception) {
            e.printStackTrace()
            try {
                networkClient.client.postgrest["reservations"]
                    .select()
                    .decodeList<Reservation>()
            } catch (e2: Exception) {
                networkClient.handleError(e2)
                emptyList()
            }
        }
    }

    suspend fun createReservation(reservation: Reservation): Reservation? {
        if (tenantSession?.isDemoMode == true) {
            val created = reservation.copy(id = "demo-res-${Clock.System.now().toEpochMilliseconds()}")
            demoReservations.add(0, created)
            return created
        }
        return try {
            networkClient.client.postgrest["reservations"]
                .insert(reservation) {
                    select() // Return the inserted row
                }
                .decodeSingleOrNull<Reservation>()
        } catch (e: Exception) {
            println("RES_REPO_ERROR: ${e.message}")
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    suspend fun updateReservation(reservation: Reservation): Reservation? {
        return try {
            @kotlinx.serialization.Serializable
            data class UpdateReservationDto(
                @kotlinx.serialization.SerialName("check_in_date") val checkInDate: String,
                @kotlinx.serialization.SerialName("check_out_date") val checkOutDate: String,
                @kotlinx.serialization.SerialName("room_type") val roomType: String?,
                @kotlinx.serialization.SerialName("special_notes") val specialNotes: String?,
                @kotlinx.serialization.SerialName("adults_count") val adultsCount: Int,
                @kotlinx.serialization.SerialName("children_count") val childrenCount: Int,
                @kotlinx.serialization.SerialName("total_price") val totalPrice: Double,
                @kotlinx.serialization.SerialName("room_id") val roomId: String?
            )
            val dto = UpdateReservationDto(
                checkInDate = reservation.checkInDate,
                checkOutDate = reservation.checkOutDate,
                roomType = reservation.roomType,
                specialNotes = reservation.specialNotes,
                adultsCount = reservation.adultsCount,
                childrenCount = reservation.childrenCount,
                totalPrice = reservation.totalPrice,
                roomId = reservation.roomId
            )
            
            networkClient.client.postgrest["reservations"]
                .update(dto) {
                    filter {
                        eq("id", reservation.id ?: "")
                    }
                    select()
                }
                .decodeSingleOrNull<Reservation>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    suspend fun updateReservationStatusAndRoom(id: String, status: String, roomId: String?) {
        try {
            @kotlinx.serialization.Serializable
            data class UpdatePayload(
                val status: String,
                @kotlinx.serialization.SerialName("room_id") val roomId: String?
            )
            
            networkClient.client.postgrest["reservations"]
                .update(UpdatePayload(status, roomId)) {
                    filter {
                        eq("id", id)
                    }
                }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    suspend fun transferFolioToNewRoom(reservationId: String, newRoomId: String) {
        try {
            @kotlinx.serialization.Serializable
            data class FolioUpdateRequest(val room_id: String)
            
            networkClient.client.postgrest["folio_items"].update(
                FolioUpdateRequest(newRoomId)
            ) {
                filter {
                    eq("reservation_id", reservationId)
                }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            // Ignore if folio_items doesn't exist yet or no items found
        }
    }

    // Call this when a guest checks out to mark as CHECKED_OUT and record the income.
    suspend fun checkOutAndPay(reservation: Reservation) {
        try {
            // 1. Mark as CHECKED_OUT
            updateReservationStatusAndRoom(reservation.id ?: "", "CHECKED_OUT", reservation.roomId)
            
            // 2. Record Income in cash_movements
            val tenantId = reservation.tenantId
            val totalAmount = reservation.totalPrice
            
            if (totalAmount > 0) {
                @kotlinx.serialization.Serializable
                data class MovementRequest(
                    val tenant_id: String,
                    val type: String,
                    val amount: Double,
                    val description: String,
                    val movement_date: String
                )
                
                val today = kotlinx.datetime.Clock.System.now().toLocalDateTime(kotlinx.datetime.TimeZone.currentSystemDefault()).date.toString()
                
                val request = MovementRequest(
                    tenant_id = tenantId,
                    type = "INCOME",
                    amount = totalAmount,
                    description = "Oda Ödemesi",
                    movement_date = today
                )
                
                networkClient.client.postgrest["cash_movements"].insert(request)
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }
    suspend fun deleteReservation(reservationId: String) {
        try {
            networkClient.client.postgrest["reservations"]
                .delete {
                    filter {
                        eq("id", reservationId)
                    }
                }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            throw e
        }
    }
}

